<template>
  <AppModal
    modal-id="categoriaRapidaModal"
    :show-modal="showModal"
    title="Nueva categoría"
    dialog-class="modal-dialog-centered"
    with-backdrop
    @close="cerrar"
  >
    <template #body>
      <div class="mb-3">
        <label class="form-label fw-semibold" for="categoria-rapida-nombre">Nombre</label>
        <input
          id="categoria-rapida-nombre"
          ref="nombreInput"
          v-model.trim="form.nombre"
          type="text"
          class="form-control"
          :disabled="guardando"
          placeholder="Ej. Bebidas sin alcohol"
          @keydown.enter.prevent="focusDescripcion"
        >
      </div>
      <div class="mb-1">
        <label class="form-label fw-semibold" for="categoria-rapida-descripcion">Descripción</label>
        <input
          id="categoria-rapida-descripcion"
          ref="descripcionInput"
          v-model.trim="form.descripcion"
          type="text"
          class="form-control"
          :disabled="guardando"
          placeholder="Para qué sirve esta categoría"
          @keydown.enter.prevent="crear"
        >
        <small class="text-muted">Nombre y descripción son obligatorios.</small>
      </div>

      <div v-if="error" class="alert alert-danger mt-3 mb-0">{{ error }}</div>
    </template>
    <template #footer>
      <button type="button" class="btn btn-outline-secondary" :disabled="guardando" @click="cerrar">
        Cancelar
      </button>
      <button type="button" class="btn btn-success" :disabled="guardando || !puedeCrear" @click="crear">
        <span v-if="guardando" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ guardando ? 'Creando...' : 'Crear' }}
      </button>
    </template>
  </AppModal>
</template>

<script>
import AppModal from './AppModal.vue';
import apiService from '../services/apiService.js';

// Alta rápida de categoría, para no tener que abandonar el alta de producto
// (RF-5 del SPEC). El componente sólo crea y avisa: quién lo monta decide qué
// hacer con la categoría nueva.
export default {
  name: 'CategoriaRapidaModal',
  components: { AppModal },
  props: {
    showModal: { type: Boolean, default: false },
    // Se usa para precargar el nombre cuando quien abre el modal ya sabe cómo
    // se debería llamar la categoría.
    nombreInicial: { type: String, default: '' }
  },
  emits: ['update:showModal', 'categoria-creada'],
  data() {
    return {
      form: { nombre: '', descripcion: '' },
      guardando: false,
      error: ''
    };
  },
  computed: {
    // `POST /api/categories` responde 400 si falta cualquiera de los dos
    // (categoryController.js). Se valida acá para no gastar un viaje.
    puedeCrear() {
      return Boolean(this.form.nombre && this.form.descripcion);
    }
  },
  watch: {
    showModal(abierto) {
      if (!abierto) return;
      this.form = { nombre: this.nombreInicial || '', descripcion: '' };
      this.error = '';
      this.guardando = false;
      this.$nextTick(() => {
        this.$refs.nombreInput?.focus?.();
        this.$refs.nombreInput?.select?.();
      });
    }
  },
  methods: {
    focusDescripcion() {
      this.$refs.descripcionInput?.focus?.();
    },
    cerrar() {
      if (this.guardando) return;
      this.$emit('update:showModal', false);
    },
    resolveErrorMessage(error) {
      return error?.userMessage
        || error?.response?.data?.message
        || error?.response?.data?.details
        || error?.message
        || 'No se pudo crear la categoría.';
    },
    async crear() {
      if (!this.puedeCrear || this.guardando) return;
      this.guardando = true;
      this.error = '';
      try {
        const { data } = await apiService.post(
          `${process.env.VUE_APP_API_BASE_URL}/api/categories`,
          { nombre: this.form.nombre, descripcion: this.form.descripcion }
        );
        // La respuesta viene envuelta por `successResponse`: `{ success, data }`.
        const categoria = data?.data || data;
        this.$emit('categoria-creada', categoria);
        this.$emit('update:showModal', false);
      } catch (error) {
        // El modal NO se cierra ante un error: cerrarlo perdería lo tipeado y
        // dejaría al operador sin saber qué corregir.
        this.error = this.resolveErrorMessage(error);
      } finally {
        this.guardando = false;
      }
    }
  }
};
</script>
